import { Controller } from 'react-hook-form';
import { MenuItem, Stack, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { UseMutationResult } from '@tanstack/react-query';
import { FormDialog } from '../../../shared/components/FormDialog';
import { ClienteSelect } from '../../../shared/components/ClienteSelect';
import { useCrudFormDialog } from '../../../shared/api/useCrudFormDialog';
import { employeeNotaFiscalInputSchema, type EmployeeNotaFiscalFormValues } from './schemas';
import type { EmployeeNotaFiscalDto, EmployeeNotaFiscalInput } from './types';

export interface EmployeeNotaFiscalFormDialogProps {
  open: boolean;
  nota: EmployeeNotaFiscalDto | null;
  hooks: {
    useCreate: () => UseMutationResult<EmployeeNotaFiscalDto, Error, EmployeeNotaFiscalInput>;
    useUpdate: () => UseMutationResult<
      EmployeeNotaFiscalDto,
      Error,
      { id: number; input: EmployeeNotaFiscalInput }
    >;
  };
  onClose: () => void;
}

const MONTHS = [
  'janeiro',
  'fevereiro',
  'marco',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
] as const;

function defaultValues(): EmployeeNotaFiscalFormValues {
  const now = new Date();
  return {
    clienteCodigo: 0,
    mes: now.getMonth() + 1,
    ano: now.getFullYear(),
  };
}

export function EmployeeNotaFiscalFormDialog({
  open,
  nota,
  hooks,
  onClose,
}: EmployeeNotaFiscalFormDialogProps): JSX.Element {
  const { t } = useTranslation();

  const { form, isEdit, onSubmit, serverError } = useCrudFormDialog<
    EmployeeNotaFiscalFormValues,
    EmployeeNotaFiscalDto,
    EmployeeNotaFiscalInput,
    number
  >({
    open,
    entity: nota,
    schema: employeeNotaFiscalInputSchema,
    defaultValues: defaultValues(),
    fromEntity: (n) => ({
      clienteCodigo: n.clienteCodigo,
      mes: typeof n.ano === 'number' ? extractMesNumerico(n) : 1,
      ano: n.ano,
    }),
    toInput: (v) => v,
    getId: (n) => n.id,
    hooks,
    messages: {
      created: t('features.colaboradores.notasFiscais.toasts.created'),
      updated: t('features.colaboradores.notasFiscais.toasts.updated'),
    },
    onClose,
  });

  return (
    <FormDialog
      open={open}
      title={
        isEdit
          ? t('features.colaboradores.notasFiscais.dialog.editTitle')
          : t('features.colaboradores.notasFiscais.dialog.newTitle')
      }
      form={form}
      onSubmit={onSubmit}
      onCancel={onClose}
      serverError={serverError}
      maxWidth="sm"
    >
      <Stack spacing={2}>
        <Controller
          control={form.control}
          name="clienteCodigo"
          render={({ field, fieldState }) => (
            <ClienteSelect
              value={field.value > 0 ? field.value : null}
              onChange={(id) => field.onChange(id ?? 0)}
              label={t('features.colaboradores.notasFiscais.fields.cliente')}
              error={Boolean(fieldState.error)}
              helperText={fieldState.error?.message}
            />
          )}
        />

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <Controller
            control={form.control}
            name="mes"
            render={({ field, fieldState }) => (
              <TextField
                select
                fullWidth
                label={t('features.colaboradores.notasFiscais.fields.mes')}
                value={Number.isFinite(field.value) ? field.value : ''}
                onChange={(e) => field.onChange(Number.parseInt(e.target.value, 10))}
                onBlur={field.onBlur}
                inputRef={field.ref}
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message ?? ' '}
              >
                {MONTHS.map((m, i) => (
                  <MenuItem key={m} value={i + 1}>
                    {t(`features.colaboradores.notasFiscais.meses.${m}`)}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />

          <Controller
            control={form.control}
            name="ano"
            render={({ field, fieldState }) => (
              <TextField
                label={t('features.colaboradores.notasFiscais.fields.ano')}
                fullWidth
                type="number"
                value={Number.isFinite(field.value) ? field.value : ''}
                onChange={(e) => {
                  const v = e.target.value;
                  field.onChange(v === '' ? Number.NaN : Number.parseInt(v, 10));
                }}
                onBlur={field.onBlur}
                inputRef={field.ref}
                inputProps={{ step: '1', min: 2000, max: new Date().getFullYear() + 1 }}
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message ?? ' '}
                sx={{ maxWidth: { sm: 140 } }}
              />
            )}
          />
        </Stack>
      </Stack>
    </FormDialog>
  );
}

/**
 * O DTO exibe `mes` como nome descritivo ("Janeiro"). Para reabrir em edição,
 * deriva o número 1..12 do nome em PT-BR. Fallback: 1.
 */
function extractMesNumerico(n: EmployeeNotaFiscalDto): number {
  const norm = (n.mes ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '');
  const idx = MONTHS.findIndex((m) => m === norm);
  return idx >= 0 ? idx + 1 : 1;
}
